import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { SideModal } from './SideModal'
import { FiLoader, FiPieChart } from 'react-icons/fi'

// Drill-down da análise por categorias do Fluxo de Caixa: os lançamentos que
// formaram o valor de uma categoria no mês.
//
// A razão de existir: quando uma fatura de cartão é RATEADA, a despesa real
// ("Combustíveis") vira uma parte dentro dela e não aparece em lista nenhuma.
// O total da categoria mostrava o valor certo e não havia como entrar nele.
// O endpoint expande o rateio (transaction_split_effective), então a parte
// aparece aqui com o valor DELA — e a soma dos itens fecha com a linha da
// categoria, que é o que torna o número conferível.

interface Item {
  transaction_id: number
  description: string
  party_name: string | null
  kind: string
  due_date: string | null
  payment_date: string | null
  payment_method: string | null
  account_name: string | null
  is_split: boolean
  situacao: 'em_aberto' | 'vencido' | 'baixado'
  valor: number
  av: number
}

const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const ptDate = (s: string | null) => (s ? s.split('-').reverse().join('/') : '—')

const SIT: Record<Item['situacao'], { label: string; cls: string }> = {
  baixado: { label: 'Baixado', cls: 'bg-primary/10 text-primary' },
  vencido: { label: 'Vencido', cls: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  em_aberto: { label: 'Em aberto', cls: 'bg-secondary text-muted-foreground' },
}

export function ItensCategoria({ categoryId, categoryName, month, kind, situacao, onClose }: {
  categoryId: number | null
  categoryName: string
  month: string
  kind?: string
  situacao?: string
  onClose: () => void
}) {
  const { authenticatedFetch } = useAuth()
  const [itens, setItens] = useState<Item[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true); setErro(null)
    try {
      const p = new URLSearchParams({ period: month })
      p.set('category_id', categoryId == null ? 'null' : String(categoryId))
      if (kind) p.set('kind', kind)
      if (situacao) p.set('situacao', situacao)
      const r = await authenticatedFetch(`/api/fluxo-caixa/categorias/itens?${p}`)
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao carregar')
      setItens(d.itens || []); setTotal(d.total || 0)
    } catch (e: any) { setErro(e.message) }
    finally { setLoading(false) }
  }, [authenticatedFetch, categoryId, month, kind, situacao])
  useEffect(() => { load() }, [load])

  const doRateio = itens.filter(i => i.is_split).length

  return (
    <SideModal
      title={categoryName}
      subtitle={`Lançamentos de ${month.split('-').reverse().join('/')} — ${itens.length} item(ns), ${brl(total)}`}
      icon={FiPieChart}
      onClose={onClose}
      maxWidth="max-w-3xl">

      {/* PLANOS (21/10/2026): os lançamentos moram numa caixa de papel com
          cabeçalho; aviso de rateio e erro viram caixas legíveis (≥13px). */}
      {erro && <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400">{erro}</div>}

      {doRateio > 0 && (
        <div className="neu-relief text-[13px] leading-relaxed rounded-xl bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/25 px-4 py-3">
          {doRateio} item(ns) vêm de um <strong>rateio</strong> — são partes de uma fatura ou pagamento
          composto, e o valor mostrado é o da parte, não o da fatura inteira.
        </div>
      )}

      <section className="neu-card rounded-xl border border-border bg-card overflow-hidden">
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap px-4 py-3 border-b border-border/60">
          <h4 className="text-[15px] font-semibold text-foreground">Lançamentos</h4>
          {!loading && itens.length > 0 && (
            <span className="text-[13px] text-muted-foreground">{itens.length} item(ns) · {brl(total)}</span>
          )}
        </div>
      {loading ? (
        <div className="py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></div>
      ) : itens.length === 0 ? (
        <div className="py-10 px-4 text-center text-muted-foreground text-sm">Nenhum lançamento nesta categoria no período.</div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground border-b border-border">
              <th className="py-2.5 pl-4 font-medium w-24">Vencimento</th>
              <th className="py-2.5 px-2 font-medium">Lançamento</th>
              <th className="py-2.5 px-2 font-medium w-28 text-right">Valor</th>
              <th className="py-2.5 px-2 font-medium w-20 text-right">AV %</th>
              <th className="py-2.5 pr-4 font-medium w-24">Situação</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((it, i) => (
              <tr key={`${it.transaction_id}-${i}`} className="border-b border-border/50">
                <td className="py-2.5 pl-4 whitespace-nowrap text-muted-foreground">{ptDate(it.due_date)}</td>
                <td className="py-2.5 px-2">
                  <div className="text-foreground">{it.description}</div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {it.is_split && (
                      <span title="Parte de um rateio — o lançamento original é maior e foi dividido entre categorias"
                        className="neu-relief text-xs rounded bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30 px-1.5 py-0.5 font-semibold">rateio</span>
                    )}
                    {it.party_name && <span className="text-xs rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">{it.party_name}</span>}
                    {it.account_name && <span className="text-xs rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">{it.account_name}</span>}
                  </div>
                </td>
                <td className="py-2.5 px-2 text-right tabular-nums font-medium text-foreground whitespace-nowrap">{brl(it.valor)}</td>
                <td className="py-2.5 px-2 text-right tabular-nums text-muted-foreground">{it.av.toFixed(1)}%</td>
                <td className="py-2.5 pr-4">
                  <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${SIT[it.situacao].cls}`}>{SIT[it.situacao].label}</span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-medium border-t border-border">
              <td className="py-2.5 pl-4 text-muted-foreground">Total</td>
              <td />
              <td className="py-2.5 px-2 text-right tabular-nums text-foreground">{brl(total)}</td>
              <td className="py-2.5 px-2 text-right text-muted-foreground">100%</td>
              <td />
            </tr>
          </tfoot>
        </table>
        </div>
      )}
      </section>
    </SideModal>
  )
}
